import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { AlertCircle, ArrowLeft, ArrowRight, ArrowUpRight, Bookmark, BookmarkCheck, Pause, RotateCcw, X } from "lucide-react";

import { CircularMeter } from "../components/atmos/Meters";
import {
  beginAttempt,
  completeAttempt,
  completionPercent,
  enterQuestion,
  formatDuration,
  getProfileBundle,
  getProfileOverview,
  pauseAttempt,
  recordAnswer,
  resumeAttempt,
  saveAttempt,
  toggleSkippedQuestion,
  unansweredQuestionIds,
} from "../lib/profileEngine";
import { DEFAULT_RETURN_URL, getReturnUrl, rememberReturnUrl } from "../lib/returnUrl";

const formatCooldownTime = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const buildCooldownMessage = (profileName, availableAt) => {
  const formatted = formatCooldownTime(availableAt);
  return {
    title: "This profile was just completed",
    message: formatted
      ? `For result integrity, ${profileName} can be started again after ${formatted}.`
      : `For result integrity, ${profileName} cannot be started again yet.`,
    helper: "Please return after the cooldown period, or review your completed report from the dashboard in the meantime.",
  };
};

const Questionnaire = () => {
  const { id: profileId } = useParams();
  const location = useLocation();
  const nav = useNavigate();
  const inviteToken = useMemo(() => new URLSearchParams(location.search).get("invite"), [location.search]);
  const launchAttemptId = useMemo(() => new URLSearchParams(location.search).get("attempt"), [location.search]);
  const [profileInfo, setProfileInfo] = useState(null);
  const [items, setItems] = useState([]);
  const [attempt, setAttempt] = useState(null);
  const [showIntro, setShowIntro] = useState(true);
  const [showPause, setShowPause] = useState(false);
  const [showJumper, setShowJumper] = useState(false);
  const [showDecisionWarning, setShowDecisionWarning] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [cooldownNotice, setCooldownNotice] = useState(null);
  const [windowReset, setWindowReset] = useState(false);
  const [completedReportId, setCompletedReportId] = useState(null);

  useEffect(() => {
    let ignore = false;

    const load = async () => {
      setLoading(true);
      setError("");
      setCooldownNotice(null);
      rememberReturnUrl(profileId, location.search);

      try {
        const bundle = await getProfileBundle(profileId, inviteToken);
        if (ignore) return;
        setProfileInfo(bundle.profile);
        setItems(bundle.items);

        const overview = await getProfileOverview(profileId, inviteToken, launchAttemptId);
        if (ignore) return;
        setAttempt(overview.activeAttempt);
        setWindowReset(Boolean(overview.windowReset));
        setCompletedReportId(overview.retakeLocked ? overview.latestCompletedAttempt?.id || null : null);
        if (overview.cooldown?.blocked) {
          setCooldownNotice(buildCooldownMessage(bundle.profile.name, overview.cooldown.availableAt));
        }
      } catch (err) {
        if (ignore) return;
        setError(err.message || "Unable to load profile.");
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      ignore = true;
    };
  }, [profileId, inviteToken, launchAttemptId, location.search]);

  const currentItem = useMemo(
    () => items.find((item) => item.id === (attempt?.currentQuestionId || items[0]?.id)) || items[0] || null,
    [items, attempt],
  );
  const unansweredIds = attempt ? unansweredQuestionIds(attempt, items) : [];
  const skippedIds = attempt?.skippedIds || [];
  const isCurrentSkipped = currentItem ? skippedIds.includes(currentItem.id) : false;
  const hasCurrentResponse = currentItem ? attempt?.answers?.[currentItem.id] !== undefined : false;
  const selectedValue = currentItem ? attempt?.answers?.[currentItem.id] : undefined;
  const progress = attempt ? Math.round(completionPercent(attempt, profileInfo?.questionCount || items.length)) : 0;
  const answered = attempt ? Object.keys(attempt.answers).length : 0;
  const hasSkippedReview = skippedIds.length > 0;

  const persist = async (nextAttempt) => {
    try {
      setError("");
      setAttempt(nextAttempt);
      const saved = await saveAttempt(profileId, nextAttempt);
      setAttempt(saved);
      return saved;
    } catch (err) {
      setError(err.message || "Unable to save profile progress.");
      throw err;
    }
  };

  const launchAttempt = async () => {
    if (!profileInfo) return;
    try {
      setError("");
      setCooldownNotice(null);
      if (attempt) {
        const resumed = attempt.status === "paused" ? resumeAttempt(attempt) : attempt;
        await persist(resumed);
        setShowIntro(false);
        return;
      }
      const created = await beginAttempt(profileId, inviteToken);
      setAttempt(created);
      setShowIntro(false);
    } catch (err) {
      if (err.status === 409) {
        const availableAt = err.payload?.detail?.availableAt || err.payload?.availableAt;
        setCooldownNotice(buildCooldownMessage(profileInfo.name, availableAt));
        setError("");
        return;
      }
      setError(err.message || "Unable to begin the profile.");
    }
  };

  const goToItem = async (itemId) => {
    if (!attempt) return;
    setShowDecisionWarning(false);
    await persist(enterQuestion(attempt, itemId));
  };

  const handleAnswer = async (value) => {
    if (!attempt || !currentItem) return;
    setShowDecisionWarning(false);

    const wasSkipped = skippedIds.includes(currentItem.id);
    let nextAttempt = recordAnswer(attempt, currentItem.id, value);

    if (wasSkipped) {
      const remainingSkippedIds = nextAttempt.skippedIds;

      if (remainingSkippedIds.length > 0) {
        const nextSkippedId =
          remainingSkippedIds.find((itemId) => itemId > currentItem.id) || remainingSkippedIds[0];
        nextAttempt = enterQuestion(nextAttempt, nextSkippedId);
      } else if (unansweredQuestionIds(nextAttempt, items).length === 0) {
        const finalItemId = items[items.length - 1]?.id || currentItem.id;
        nextAttempt = enterQuestion(nextAttempt, finalItemId);
      }
    }

    await persist(nextAttempt);
  };

  const handlePrevious = async () => {
    if (!attempt || !currentItem) return;
    const previous = [...items].reverse().find((item) => item.id < currentItem.id);
    if (previous) await goToItem(previous.id);
  };

  const handleNext = async () => {
    if (!attempt || !currentItem || !profileInfo) return;
    if (!hasCurrentResponse && !isCurrentSkipped) {
      setShowDecisionWarning(true);
      return;
    }

    const next = items.find((item) => item.id > currentItem.id);
    if (!next) {
      if (unansweredIds.length > 0) {
        await goToItem(unansweredIds[0]);
        return;
      }
      try {
        const completed = await completeAttempt(profileId, attempt);
        setAttempt(completed);
        nav(`/report/${profileInfo.id}?attempt=${completed.id}`);
      } catch (err) {
        setError(err.message || "Unable to complete the profile.");
      }
      return;
    }
    await goToItem(next.id);
  };

  const handlePause = async () => {
    if (!attempt) return;
    await persist(pauseAttempt(attempt));
    setShowPause(false);
    nav("/dashboard");
  };

  const handleToggleSkip = async () => {
    if (!attempt || !currentItem) return;
    setShowDecisionWarning(false);
    await persist(toggleSkippedQuestion(attempt, currentItem.id));
  };

  if (loading) {
    return <main className="relative min-h-[calc(100vh-3.5rem)] lg:min-h-screen flex items-center justify-center text-ink-2 lg:pl-24 xl:pl-28">Loading profile...</main>;
  }

  if (!profileInfo) {
    return <main className="relative min-h-[calc(100vh-3.5rem)] lg:min-h-screen flex items-center justify-center text-[color:var(--warn-text)] lg:pl-24 xl:pl-28">{error || "Profile unavailable."}</main>;
  }

  if (showIntro) {
    return (
      <main className="relative min-h-[calc(100vh-3.5rem)] lg:min-h-screen flex items-center justify-center px-4 md:px-6 py-28 md:py-10 lg:pl-24 xl:pl-28" data-testid="assessment-intro">
        <div className="absolute inset-0">
          <div className="orb orb-amber w-[340px] h-[340px] md:w-[600px] md:h-[600px] top-[-120px] md:top-[-180px] left-[-120px] md:left-[-160px] float-slow" />
          <div className="orb orb-teal w-[300px] h-[300px] md:w-[520px] md:h-[520px] bottom-[-120px] md:bottom-[-160px] right-[-100px] md:right-[-140px] float-slow" style={{ animationDelay: "-4s" }} />
        </div>
        <div className="relative glass-strong rounded-sm p-5 md:p-12 max-w-5xl w-full">
          <div className="font-mono text-[16px] tracking-widest-2 text-gold mb-3">◇ PRE-PROFILE WINDOW</div>
          <h1 className="font-serif text-3xl md:text-6xl text-ink leading-tight">
            {profileInfo.name}
            <br />
            <span className="italic text-gold">before you begin.</span>
          </h1>
          <p className="text-ink-2 mt-5 max-w-3xl leading-relaxed">{profileInfo.tagline}</p>
          {cooldownNotice ? (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-6 rounded-sm border border-amber-300/30 bg-amber-300/10 p-4 md:p-5 text-amber-100"
            >
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 mt-0.5 shrink-0" />
                <div>
                  <div className="font-mono text-[16px] tracking-widest-2 uppercase text-[color:var(--warn-text)]">
                    Cooldown in effect
                  </div>
                  <div className="mt-2 font-serif text-2xl text-ink">{cooldownNotice.title}</div>
                  <p className="mt-2 text-base md:text-base leading-relaxed text-amber-100/90">
                    {cooldownNotice.message}
                  </p>
                  <p className="mt-2 text-base leading-relaxed text-amber-100/75">
                    {cooldownNotice.helper}
                  </p>
                </div>
              </div>
            </motion.div>
          ) : null}
          {error ? <div className="mt-4 text-base text-[color:var(--warn-text)]">{error}</div> : null}
          {completedReportId ? (
            <div className="mt-6 rounded-sm border border-line p-4 md:p-5 text-ink-2" data-testid="retake-locked">
              You have already completed this profile. To take it again, purchase a retake through your Redacamy course.
            </div>
          ) : null}
          {attempt?.windowEnds ? (
            <div className="mt-6 rounded-sm border border-line p-4 md:p-5 text-ink-2" data-testid="completion-window">
              {windowReset ? (
                <p className="mb-2 text-[color:var(--warn-text)]">
                  Your earlier answers were older than 8 days, so this profile has restarted from the beginning. Your
                  purchase is still valid — nothing more to pay.
                </p>
              ) : null}
              <p>
                Complete by{" "}
                {new Date(attempt.windowEnds).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}{" "}
                or the profile restarts from the beginning.
              </p>
            </div>
          ) : null}

          <div className="grid grid-cols-12 gap-4 md:gap-6 mt-8 md:mt-10">
            <div className="col-span-12 lg:col-span-7 glass rounded-sm p-6">
              <div className="font-mono text-[16px] tracking-widest-2 text-ink mb-3">◇ INSTRUCTIONS</div>
              <div className="space-y-3 text-ink-2 leading-relaxed">
                {profileInfo.instructions.map((item) => (
                  <p key={item}>{item}</p>
                ))}
              </div>
            </div>
            <div className="col-span-12 lg:col-span-5 glass rounded-sm p-6">
              <div className="font-mono text-[16px] tracking-widest-2 text-ink mb-3">◇ SCALE</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-base text-ink-2">
                {profileInfo.scale.map((item) => (
                  <div
                    key={item.value}
                    className="grid grid-cols-[1.5rem_minmax(0,1fr)] items-start gap-x-3 gap-y-1 border-b border-line pb-2 min-h-[3.25rem]"
                  >
                    <span className="font-mono text-gold leading-none pt-0.5">{item.value}</span>
                    <span className="leading-snug break-words">{item.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-3 mt-10">
            {completedReportId ? (
              <Link to={`/report/${profileId}?attempt=${completedReportId}`} className="btn-gold">Open Your Report</Link>
            ) : cooldownNotice ? (
              <Link to="/dashboard" className="btn-gold">Open Dashboard</Link>
            ) : (
              <button onClick={launchAttempt} className="btn-gold">
                {attempt ? "Resume Current Profile" : `Start ${profileInfo.shortName} Profile`}
              </button>
            )}
            <Link to="/dashboard" className="btn-ghost">Return to Dashboard</Link>
            <a href={getReturnUrl(profileId) || DEFAULT_RETURN_URL} className="btn-ghost" data-testid="return-to-redacamy-intro">
              <ArrowUpRight className="w-4 h-4" /> Return to Redacamy
            </a>
          </div>
        </div>
      </main>
    );
  }

  if (!attempt || !currentItem) return null;

  return (
    <main className="relative min-h-[calc(100vh-3.5rem)] lg:min-h-screen flex flex-col lg:pl-24 xl:pl-28" data-testid="questionnaire">
      <div className="fixed top-0 inset-x-0 z-50">
        <div className="meter-track" style={{ height: "2px" }}>
          <div className="meter-fill" style={{ width: `${progress}%` }} />
        </div>
      </div>

      <header className="px-4 md:px-12 lg:px-20 pt-6 md:pt-8 pb-5 md:pb-6 flex flex-wrap items-center justify-between gap-3" data-testid="qa-topbar">
        <div className="flex items-center gap-3 md:gap-4">
          <button onClick={() => nav("/dashboard")} className="text-ink-2 hover:text-gold">
            <X className="w-5 h-5" />
          </button>
          <button
            type="button"
            onClick={() => setShowJumper(true)}
            className="hidden sm:block font-mono text-[16px] md:text-[16px] tracking-widest-2 text-ink hover:text-gold transition-colors"
          >
            {profileInfo.shortName} · {currentItem.sectionLabel.toUpperCase()}
          </button>
        </div>

        <a
          href={getReturnUrl(profileId) || DEFAULT_RETURN_URL}
          className="btn-ghost justify-center"
          data-testid="return-to-redacamy-test"
          title="Your answers are saved. You can come back and continue within 8 days of purchase."
        >
          <ArrowUpRight className="w-4 h-4" /> Return to Redacamy
        </a>
      </header>

      <section className="flex-1 grid grid-cols-12 gap-6 md:gap-8 px-4 md:px-12 lg:px-20 items-start lg:items-center pb-24 md:pb-32">
        <div className="col-span-12 lg:col-span-9">
          <div className="font-mono text-[16px] tracking-widest-2 text-gold mb-4">
            ◇ {currentItem.sectionLabel.toUpperCase()}
            {profileId !== "ssp" ? ` · ${currentItem.scaleLabel.toUpperCase()}` : ""}
          </div>
          <AnimatePresence mode="wait">
            <motion.h1
              key={currentItem.id}
              initial={{ opacity: 0, y: 18, filter: "blur(6px)" }}
              animate={{ opacity: 1, y: 0, filter: "blur(0)" }}
              exit={{ opacity: 0, y: -10, filter: "blur(4px)" }}
              transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
              className="font-serif text-3xl md:text-6xl leading-[1.05] tracking-tight text-ink"
            >
              {currentItem.text}
            </motion.h1>
          </AnimatePresence>

          <p className="text-ink mt-6 font-mono text-[16px] tracking-widest-2">
            ◇ RESPOND ON THE 1—10 SCALE
          </p>

          {isCurrentSkipped ? (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-5 inline-flex items-center gap-2 rounded-sm border border-gold/40 bg-gold/10 px-3 py-2 text-gold"
            >
              <AlertCircle className="h-4 w-4" />
              <span className="font-mono text-[16px] tracking-widest-2 uppercase">
                This statement is currently marked as skipped
              </span>
            </motion.div>
          ) : null}

          {showDecisionWarning ? (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-4 inline-flex items-center gap-2 rounded-sm border border-[#8ea2c4]/35 bg-[#8ea2c4]/10 px-3 py-2 text-[color:var(--info-text)]"
            >
              <AlertCircle className="h-4 w-4" />
              <span className="font-mono text-[16px] tracking-widest-2 uppercase">
                Respond or mark this item as skipped before continuing
              </span>
            </motion.div>
          ) : null}

          {error ? (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-4 inline-flex items-center gap-2 rounded-sm border border-amber-300/35 bg-amber-300/10 px-3 py-2 text-[color:var(--warn-text)]"
            >
              <AlertCircle className="h-4 w-4" />
              <span className="font-mono text-[16px] tracking-widest-2 uppercase">{error}</span>
            </motion.div>
          ) : null}

          <div className="flex flex-wrap gap-3 mt-8">
            {profileInfo.scale.map((item) => {
              const selected = selectedValue === item.value;
              return (
                <button
                  key={item.value}
                  className={`chip ${selected ? "selected" : ""}`}
                  onClick={() => handleAnswer(item.value)}
                >
                  {item.value}
                </button>
              );
            })}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-3 mt-8">
            {profileInfo.scale.map((item) => {
              const selected = selectedValue === item.value;
              return (
                <div
                  key={item.value}
                  className={`text-base font-mono border rounded-sm px-3 py-2 transition-all bg-[color:var(--tile-bg)] ${
                    selected
                      ? "border-gold bg-gold/20 text-ink font-medium shadow-[inset_0_0_0_1px_rgba(221,167,79,0.25)]"
                      : "border-line text-[color:var(--tile-ink)]"
                  }`}
                >
                  <span className={selected ? "text-gold" : "text-ink-2"}>{item.value}</span> · {item.label}
                </div>
              );
            })}
          </div>

          <div
            className={`mt-10 md:mt-12 grid w-full gap-3 ${
              hasSkippedReview ? "grid-cols-2 xl:grid-cols-5" : "grid-cols-2 xl:grid-cols-4"
            }`}
          >
            <button
              onClick={handlePrevious}
              disabled={currentItem.id === 1}
              className="btn-ghost w-full min-h-[76px] justify-center px-4 text-center text-[16px] md:text-[16px] leading-tight disabled:opacity-30"
            >
              <ArrowLeft className="w-4 h-4 shrink-0" /> Prev
            </button>
            {hasSkippedReview ? (
              <button
                onClick={() => goToItem(skippedIds[0])}
                className="btn-ghost w-full min-h-[76px] justify-center px-4 text-center text-[16px] md:text-[16px] leading-tight whitespace-normal"
              >
                <RotateCcw className="w-4 h-4 shrink-0" /> Return to skipped item
              </button>
            ) : null}
            <button
              onClick={handleToggleSkip}
              className={`btn-ghost w-full min-h-[76px] justify-center px-4 text-center text-[16px] md:text-[16px] leading-tight ${
                skippedIds.includes(currentItem.id)
                  ? "border-gold text-gold bg-gold/10 hover:bg-gold/15"
                  : ""
              }`}
            >
              {skippedIds.includes(currentItem.id) ? <BookmarkCheck className="w-4 h-4 shrink-0" /> : <Bookmark className="w-4 h-4 shrink-0" />}
              {skippedIds.includes(currentItem.id) ? "Skipped" : "Skip"}
            </button>
            <button
              onClick={() => setShowPause(true)}
              className="btn-ghost w-full min-h-[76px] justify-center px-4 text-center text-[16px] md:text-[16px] leading-tight"
            >
              <Pause className="w-4 h-4 shrink-0" /> Pause
            </button>
            <button
              onClick={handleNext}
              className="btn-gold w-full min-h-[76px] justify-center px-4 text-center text-[16px] md:text-[16px] leading-tight"
            >
              {currentItem.id === profileInfo.questionCount && unansweredIds.length === 0 ? "Complete" : "Continue"} <ArrowRight className="w-4 h-4 shrink-0" />
            </button>
          </div>
        </div>

        <aside className="hidden lg:flex col-span-3 flex-col items-center gap-6">
          <CircularMeter value={progress} size={200} stroke={5} label="PROGRESS" />
          <div className="w-full glass rounded-sm p-4">
            <div className="font-mono text-[16px] tracking-widest-2 text-ink mb-2">◇ RESPONDED</div>
            <div className="font-mono text-2xl text-ink tabular">
              {answered} <span className="text-ink text-base">/ {profileInfo.questionCount}</span>
            </div>
            <div className="meter-track mt-3"><div className="meter-fill" style={{ width: `${progress}%` }} /></div>
          </div>
          <div className="w-full glass rounded-sm p-4">
            <div className="font-mono text-[16px] tracking-widest-2 text-ink mb-2">◇ TRACKING</div>
            <div className="text-base text-ink-2 leading-relaxed">
              Active time · {formatDuration(attempt.analytics.totalActiveMs)}
            </div>
            <div className="text-base text-ink-2 leading-relaxed mt-2">
              Skipped · {skippedIds.length}
            </div>
            {skippedIds.length > 0 ? (
              <div className="mt-3 flex flex-wrap gap-2">
                {skippedIds.slice(0, 8).map((itemId) => (
                  <button
                    key={itemId}
                    type="button"
                    onClick={() => goToItem(itemId)}
                    className="inline-flex items-center rounded-full border border-gold/35 bg-gold/10 px-2.5 py-1 font-mono text-[16px] tracking-widest-2 text-gold transition-all hover:bg-gold/15 hover:border-gold/50"
                  >
                    {String(itemId).padStart(2, "0")}
                  </button>
                ))}
                {skippedIds.length > 8 ? (
                  <span className="inline-flex items-center rounded-full border border-line px-2.5 py-1 font-mono text-[16px] tracking-widest-2 text-ink">
                    +{skippedIds.length - 8}
                  </span>
                ) : null}
              </div>
            ) : null}
          </div>
        </aside>
      </section>

      <AnimatePresence>
        {showJumper ? (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowJumper(false)}
          >
            <div className="absolute inset-0 bg-black/80 backdrop-blur-xl" />
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 20, opacity: 0 }}
              transition={{ duration: 0.45 }}
              className="relative glass-strong rounded-sm p-5 md:p-8 max-w-3xl w-full mx-4 md:mx-6 max-h-[80vh] overflow-y-auto"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="font-mono text-[16px] tracking-widest-2 text-ink mb-2">◇ NAVIGATE</div>
              <h3 className="font-serif text-2xl md:text-3xl text-ink mb-6">Jump to any item</h3>
              <div className="mb-5 flex flex-wrap items-center gap-3 text-[16px] font-mono tracking-widest-2 text-ink">
                <span className="inline-flex items-center gap-2">
                  <span className="h-2.5 w-2.5 rounded-full bg-yellow-300" />
                  CURRENT
                </span>
                <span className="inline-flex items-center gap-2">
                  <span className="h-2.5 w-2.5 rounded-full bg-red-400" />
                  SKIPPED
                </span>
                <span className="inline-flex items-center gap-2">
                  <span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
                  RESPONDED
                </span>
              </div>
              <div className="grid grid-cols-5 sm:grid-cols-8 md:grid-cols-10 gap-2">
                {items.map((item) => {
                  const answeredItem = attempt.answers[item.id] !== undefined;
                  const isSkipped = skippedIds.includes(item.id);
                  const isCurrent = item.id === currentItem.id;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => {
                        goToItem(item.id);
                        setShowJumper(false);
                      }}
                      className={`relative rounded-sm border px-3 py-3 font-mono text-[16px] tracking-widest-2 transition-all ${
                        isCurrent
                          ? "border-yellow-300 bg-yellow-300/12 text-[color:var(--warn-text)] shadow-[0_0_0_1px_rgba(253,224,71,0.18)]"
                          : isSkipped
                            ? "border-red-400/50 bg-red-400/10 text-[color:var(--danger-text)]"
                            : answeredItem
                              ? "border-emerald-400/45 bg-emerald-400/10 text-[color:var(--success-text)]"
                              : "border-line text-ink-3"
                      }`}
                    >
                      {(isCurrent || isSkipped) ? (
                        <span
                          className={`absolute right-1.5 top-1.5 h-2.5 w-2.5 rounded-full ${
                            isCurrent ? "bg-yellow-300" : "bg-red-400"
                          }`}
                        />
                      ) : null}
                      {String(item.id).padStart(2, "0")}
                    </button>
                  );
                })}
              </div>
            </motion.div>
          </motion.div>
        ) : null}
      </AnimatePresence>

      <AnimatePresence>
        {showPause ? (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowPause(false)}
          >
            <div className="absolute inset-0 bg-black/70 backdrop-blur-xl" />
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 20, opacity: 0 }}
              transition={{ duration: 0.45 }}
              className="relative glass-strong rounded-sm p-7 max-w-lg w-full mx-4"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="font-mono text-[16px] tracking-widest-2 text-ink mb-3">◇ PAUSE PROFILE</div>
              <h3 className="font-serif text-3xl text-ink">Pause this profile?</h3>
              <p className="text-ink-2 mt-4 leading-relaxed">
                Your responses, skipped items, and active timing logs will be saved so you can resume later.
              </p>
              <div className="flex flex-wrap gap-3 mt-7">
                <button className="btn-gold" onClick={handlePause}>Save and Pause</button>
                <button className="btn-ghost" onClick={() => setShowPause(false)}>Continue Responding</button>
              </div>
            </motion.div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </main>
  );
};

export default Questionnaire;
